import { fileExtension } from '../mediaViewer/mediaFileTypes';

export type DocumentEditorKind = 'spreadsheet' | 'richedit';

/** Excel-family extensions editable/previewable in the DevExpress Spreadsheet control. */
const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls', 'xlsm', 'csv', 'tsv']);

/** Word-family extensions editable/previewable in the DevExpress RichEdit control. */
const RICHEDIT_EXTENSIONS = new Set(['docx', 'doc', 'rtf']);

export const SUPPORTED_SPREADSHEET_EXTENSIONS_LABEL = 'XLSX, XLS, XLSM, CSV, TSV';
export const SUPPORTED_RICHEDIT_EXTENSIONS_LABEL = 'DOCX, DOC, RTF';

/**
 * Editor Suite decision: keep DevExpress Spreadsheet for sheets/CSV.
 * Univer / `.jsheet` is intentionally not used in this monorepo.
 * CSV/TSV caveat: saving writes the active sheet only when multiple sheets exist.
 */
export const SPREADSHEET_SURFACE_NOTE =
  'DevExpress Spreadsheet (Univer/JSheet skipped). CSV/TSV save writes the active sheet only.';

export function isSpreadsheetFile(fileName: string): boolean {
  return SPREADSHEET_EXTENSIONS.has(fileExtension(fileName));
}

export function isRichEditFile(fileName: string): boolean {
  return RICHEDIT_EXTENSIONS.has(fileExtension(fileName));
}

export function isOfficeFile(fileName: string): boolean {
  return isSpreadsheetFile(fileName) || isRichEditFile(fileName);
}

/** Preferred preview editor for a file: spreadsheet for Excel/CSV, RichEdit for Word/RTF. */
export function getOfficePreviewKind(fileName: string): DocumentEditorKind | null {
  const ext = fileExtension(fileName);
  if (SPREADSHEET_EXTENSIONS.has(ext)) return 'spreadsheet';
  if (RICHEDIT_EXTENSIONS.has(ext)) return 'richedit';
  return null;
}

export function officeFlagsForItem(item: { isDirectory?: boolean; name: string } | undefined): {
  spreadsheet: boolean;
  richedit: boolean;
} {
  if (!item || item.isDirectory) return { spreadsheet: false, richedit: false };
  return { spreadsheet: isSpreadsheetFile(item.name), richedit: isRichEditFile(item.name) };
}

/** Accept attribute for File → Open → From PC, scoped to the current editor kind. */
export function officeFileInputAccept(kind: DocumentEditorKind): string {
  const exts = kind === 'spreadsheet' ? SPREADSHEET_EXTENSIONS : RICHEDIT_EXTENSIONS;
  return Array.from(exts)
    .map((ext) => `.${ext}`)
    .join(',');
}

export function isAllowedOfficeFileForKind(fileName: string, kind: DocumentEditorKind): boolean {
  return kind === 'spreadsheet' ? isSpreadsheetFile(fileName) : isRichEditFile(fileName);
}
